import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { apiRequest, formatPrice } from "../api/client";
import Button from "../components/Button";
import { brand, coachStats, images, testimonials } from "../data/siteData";
import transformation1 from "../assets/moataz trasnformation assets/A3349F50-B4fdsa46-46CF-8795-7E204A88221F.JPG";
import transformation2 from "../assets/moataz trasnformation assets/CFC13A54-FfdasB07-4B1B-8255-F1C491E665BB.JPEG";
import transformation3 from "../assets/moataz trasnformation assets/IMG_1537.jpg";
import transformation4 from "../assets/moataz trasnformation assets/FEE76A8E-2F5C-4672-A618-32D88FEF885E.JPG";
import transformation5 from "../assets/moataz trasnformation assets/930FAD37-FC8E-424E-8947-BFC0E0D5A713.JPEG";
import transformation6 from "../assets/moataz trasnformation assets/1178ED6E-E190-42EE-B52D-89EEEC53B0AB.JPEG";
import transformation7 from "../assets/moataz trasnformation assets/863C627E-B65F-4574-8932-DAE8B7704B18.JPG";
import transformation8 from "../assets/moataz trasnformation assets/244D3679-A757-4ECC-85A8-024777D063F1.JPEG";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0 },
};

const stagger = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.09,
    },
  },
};

const blueprint = [
  {
    number: "01",
    title: "Precision macro calculations",
    text: "Learn how to set repeatable protein, fats, and carbs specifically for your metabolic rate and training volume.",
    image: images.macro,
    tag: "Macros",
    large: true,
  },
  {
    number: "02",
    title: "Meal planning",
    text: "Done-for-you frameworks for shredding, lean gain, grocery planning, and weekly prep.",
    image: images.mealPlanning,
    tag: "Structure",
  },
  {
    number: "03",
    title: "Supplementation",
    text: "The science of what actually works and what is a waste of money.",
    image: images.supliment,
    tag: "Clarity",
  },
  {
    number: "04",
    title: "100+ custom recipes",
    text: "High-protein, high-flavor meals that make sticking to your diet effortless and enjoyable.",
    image: images.customRecipes,
    tag: "Recipes",
    span: true,
  },
];

const heroProofItems = [
  { icon: "↓", label: "Digital download" },
  { icon: "%", label: "Nutrition formulas" },
  { icon: "+", label: "Macros & recipes" },
];

const transformations = [
  { name: "Ahmed L.", time: "10 weeks", image: transformation1 },
  { name: "Omar S.", time: "6 months", image: transformation2 },
  { name: "Karim M.", time: "14 weeks", image: transformation3 },
  { name: "Zack C.", time: "8 weeks", image: transformation4 },
  { name: "Youssef A.", time: "12 weeks", image: transformation5 },
  { name: "Mostafa K.", time: "16 weeks", image: transformation6 },
  { name: "Ali R.", time: "20 weeks", image: transformation7 },
  { name: "Hassan M.", time: "90 days", image: transformation8 },
];

const videos = [
  {
    title: "High nutrition day",
    poster: images.coach,
    src: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4",
  },
  {
    title: "Full meal rules",
    poster: images.hero,
    src: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerEscapes.mp4",
  },
  {
    title: "Mindset & gains",
    poster: images.gym,
    src: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerFun.mp4",
  },
  {
    title: "Off-season fueling",
    poster: images.detail,
    src: "https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerJoyrides.mp4",
  },
];

const fallbackFaqs = [
  {
    question: "Who is the eBook for?",
    answer:
      "It is built for lifters who want a clear nutrition system for fat loss, lean muscle gain, meal planning, and macro tracking without relying on random diet rules.",
  },
  {
    question: "What do I get after buying the book?",
    answer:
      "You get instant access to the digital nutrition blueprint, including macro guidance, meal-planning frameworks, supplement clarity, and high-protein recipe ideas.",
  },
  {
    question: "Can beginners use the nutrition plan?",
    answer:
      "Yes. The book breaks the process into practical steps, so beginners can set targets confidently while more advanced athletes can refine their current approach.",
  },
  {
    question: "How is coaching different from the eBook?",
    answer:
      "The eBook gives you the complete framework to follow on your own. Coaching adds personal feedback, adjustments, accountability, and a plan shaped around your body, schedule, and goals.",
  },
  {
    question: "Do I need coaching to get results from the book?",
    answer:
      "No. The book is designed to be used independently. Coaching is best if you want closer guidance, faster troubleshooting, and ongoing support from Moataz.",
  },
  {
    question: "How do I ask about coaching availability?",
    answer:
      "Use the contact page to send your goals, current training level, and what you need help with. The team can then guide you on the best next step.",
  },
];

export default function Home() {
  const feedbackCarouselRef = useRef(null);
  const [settings, setSettings] = useState(null);
  const [dynamicTestimonials, setDynamicTestimonials] = useState([]);
  const [dynamicFaqs, setDynamicFaqs] = useState([]);
  const [featuredProduct, setFeaturedProduct] = useState(null);
  const [activeTransformation, setActiveTransformation] = useState(0);

  useEffect(() => {
    Promise.all([
      apiRequest("/api/settings/public"),
      apiRequest("/api/testimonials"),
      apiRequest("/api/faqs"),
      apiRequest("/api/products?featured=true"),
    ])
      .then(([settingsResponse, testimonialResponse, faqResponse, productResponse]) => {
        setSettings(settingsResponse.data);
        setDynamicTestimonials(testimonialResponse.data || []);
        setDynamicFaqs(faqResponse.data || []);
        setFeaturedProduct(productResponse.data?.[0] || null);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const mobileQuery = window.matchMedia("(max-width: 960px)");
    let intervalId;

    const syncMobileLoop = () => {
      window.clearInterval(intervalId);
      if (!mobileQuery.matches) return;

      intervalId = window.setInterval(() => {
        setActiveTransformation((index) => (index + 1) % transformations.length);
      }, 4200);
    };

    syncMobileLoop();
    if (mobileQuery.addEventListener) {
      mobileQuery.addEventListener("change", syncMobileLoop);
    } else {
      mobileQuery.addListener(syncMobileLoop);
    }

    return () => {
      window.clearInterval(intervalId);
      if (mobileQuery.removeEventListener) {
        mobileQuery.removeEventListener("change", syncMobileLoop);
      } else {
        mobileQuery.removeListener(syncMobileLoop);
      }
    };
  }, []);

  const scrollCarousel = (ref, direction) => {
    const carousel = ref.current;
    if (!carousel) return;

    const cards = Array.from(carousel.querySelectorAll(".feedback-card"));
    if (!cards.length) return;

    const carouselLeft = carousel.getBoundingClientRect().left;
    const maxScroll = carousel.scrollWidth - carousel.clientWidth;
    const positions = cards.map((card) => Math.min(card.getBoundingClientRect().left - carouselLeft + carousel.scrollLeft, maxScroll));
    const currentIndex = positions.reduce((closestIndex, position, index) => {
      const currentDistance = Math.abs(position - carousel.scrollLeft);
      const closestDistance = Math.abs(positions[closestIndex] - carousel.scrollLeft);
      return currentDistance <= closestDistance ? index : closestIndex;
    }, 0);
    const nextIndex = (currentIndex + direction + cards.length) % cards.length;

    carousel.scrollTo({
      left: positions[nextIndex],
      behavior: "smooth",
    });
  };

  const moveTransformation = (direction) => {
    setActiveTransformation((index) => (index + direction + transformations.length) % transformations.length);
  };

  const content = settings?.content || {};
  const proofItems = content.heroProofItems || heroProofItems;
  const feedbackItems = dynamicTestimonials.length
    ? dynamicTestimonials.map((item) => ({
        name: item.customer_name,
        role: item.role || "Verified customer",
        quote: item.review_text,
      }))
    : testimonials;
  const faqItems = dynamicFaqs.length
    ? dynamicFaqs.map((item) => ({ question: item.question, answer: item.answer }))
    : fallbackFaqs;
  const dynamicBlueprint = content.blueprint?.length
    ? content.blueprint.map((item, index) => ({
        ...blueprint[index % blueprint.length],
        ...item,
        image: blueprint[index % blueprint.length].image,
      }))
    : blueprint;
  const activePrice = featuredProduct ? formatPrice(featuredProduct.discount_price || featuredProduct.price) : brand.price;
  const oldPrice = featuredProduct?.discount_price ? formatPrice(featuredProduct.price) : brand.oldPrice;

  return (
    <div className="home-page">
      <section className="home-hero">
        <motion.div className="hero-noise" animate={{ opacity: [0.18, 0.28, 0.18] }} transition={{ duration: 5, repeat: Infinity }} />
        <div className="container home-hero-grid">
          <motion.div className="home-hero-copy" initial="hidden" animate="show" variants={stagger}>
            <motion.h1 variants={fadeUp}>
              {settings?.hero_title || "Master your nutrition. Build your best physique."}
            </motion.h1>
            <motion.p variants={fadeUp}>
              {settings?.hero_subtitle || "Stop guessing. Start growing. The ultimate science-based blueprint for bodybuilding nutrition and elite conditioning."}
            </motion.p>
            <motion.div className="home-actions" variants={fadeUp}>
              <Button to="/shop">{settings?.hero_button_text || "Buy the eBook"}</Button>
              {/* <Button to="/ebook" variant="secondary">Preview Inside</Button> */}
            </motion.div>
            <motion.div className="hero-proof" variants={fadeUp}>
              {proofItems.map((item) => (
                <span key={item.label}>
                  <i aria-hidden="true">{item.icon}</i>
                  {item.label}
                </span>
              ))}
            </motion.div>
          </motion.div>

          <motion.div
            className="home-hero-card"
            initial={{ opacity: 0, x: 42, rotate: 1 }}
            animate={{ opacity: 1, x: 0, rotate: 0 }}
            transition={{ duration: 0.75, ease: "easeOut" }}
          >
            <motion.span className="hero-orbit" animate={{ y: [-8, 8, -8] }} transition={{ duration: 3.4, repeat: Infinity, ease: "easeInOut" }} />
            <img src={images.hero} alt="Moataz Hafez coach portrait" />
          </motion.div>
        </div>
      </section>

      <section className="home-section blueprint-section">
        <div className="container">
          <SectionHeading title="Inside the eBook" />
          <motion.div className="blueprint-grid" initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.24 }} variants={stagger}>
            {dynamicBlueprint.map((item) => (
              <motion.article
                className={`blueprint-card${item.large ? " blueprint-wide" : ""}${item.span ? " blueprint-span-2" : ""}`}
                style={{ "--blueprint-image": `url(${item.image})` }}
                variants={fadeUp}
                whileHover={{ y: -8 }}
                transition={{ duration: 0.28, ease: "easeOut" }}
                key={item.title}
              >
                <div className="blueprint-card-top">
                  <span className="blueprint-number">{item.number}</span>
                  <span className="blueprint-tag">{item.tag}</span>
                </div>
                <div className="blueprint-card-copy">
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </div>
              </motion.article>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="home-section progress-section" id="results">
        <div className="container progress-grid">
          <div>
            <h2>Real people.<br /><span>Real progress.</span></h2>
          </div>
          <p>
            Thousands have used this methodology to transform their lives. No fads, just results driven by discipline and science.
          </p>
        </div>
        <div className="container carousel-block">
          <div className="transformation-carousel transformation-desktop-carousel">
            <div className="transformation-track">
              {transformations.map((item) => (
                <article className="transformation-card" key={`${item.name}-${item.time}`}>
                  <img src={item.image} alt={`${item.name} transformation`} />
                  <div>
                    <strong>{item.name} - {item.time}</strong>
                    <span>★★★★★</span>
                  </div>
                </article>
              ))}
              {transformations.map((item) => (
                <article className="transformation-card" key={`${item.name}-${item.time}-loop`} aria-hidden="true">
                  <img src={item.image} alt="" />
                  <div>
                    <strong>{item.name} - {item.time}</strong>
                    <span>★★★★★</span>
                  </div>
                </article>
              ))}
            </div>
          </div>
          <div className="mobile-transformation-carousel" aria-label="Transformation progress carousel">
            <div className="mobile-transformation-viewport">
              <div className="mobile-transformation-track" style={{ transform: `translateX(-${activeTransformation * 100}%)` }}>
                {transformations.map((item, index) => (
                  <article
                    className="transformation-card mobile-transformation-card"
                    key={`${item.name}-${item.time}-mobile`}
                    aria-hidden={activeTransformation !== index}
                  >
                    <img src={item.image} alt={`${item.name} transformation`} />
                    <div>
                      <strong>{item.name} - {item.time}</strong>
                      <span>★★★★★</span>
                    </div>
                  </article>
                ))}
              </div>
            </div>
            <div className="mobile-transformation-controls" aria-label="Transformation carousel controls">
              <button type="button" aria-label="Previous transformation" onClick={() => moveTransformation(-1)}>&larr;</button>
              <div className="mobile-transformation-dots" aria-hidden="true">
                {transformations.map((item, index) => (
                  <span className={activeTransformation === index ? "active" : ""} key={`${item.name}-dot`} />
                ))}
              </div>
              <button type="button" aria-label="Next transformation" onClick={() => moveTransformation(1)}>&rarr;</button>
            </div>
          </div>
        </div>
      </section>

      <section className="home-section">
        <div className="container feedback-section-head">
          <SectionHeading title="Community feedback" />
          <div className="carousel-controls" aria-label="Feedback carousel controls">
            <button type="button" aria-label="Previous feedback" onClick={() => scrollCarousel(feedbackCarouselRef, -1)}>&larr;</button>
            <button type="button" aria-label="Next feedback" onClick={() => scrollCarousel(feedbackCarouselRef, 1)}>&rarr;</button>
          </div>
        </div>
        <div className="container">
          <motion.div className="feedback-carousel" initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.28 }} variants={stagger} ref={feedbackCarouselRef}>
            <div className="feedback-track">
              {feedbackItems.map((item) => (
                <motion.article className="feedback-card" variants={fadeUp} key={item.name}>
                  <span>★★★★★</span>
                  <p>"{item.quote}"</p>
                  <strong>{item.name}</strong>
                  <small>{item.role}</small>
                </motion.article>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="home-section mastery-section" id="videos">
        <div className="container">
          <h2>Motivation & mastery</h2>
          <motion.div className="video-grid" initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.2 }} variants={stagger}>
            {videos.map((video) => (
              <VideoCard video={video} key={video.title} />
            ))}
          </motion.div>
        </div>
      </section>

      <section className="coach-band">
        <div className="container coach-grid">
          <motion.div className="coach-frame" initial={{ opacity: 0, x: -32 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.55 }}>
            <img src={images.detail} alt="Moataz Hafez full body portrait" />
          </motion.div>
          <motion.div className="coach-copy" initial={{ opacity: 0, x: 32 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.55 }}>
            <span className="eyebrow">Meet the coach</span>
            <h2>{settings?.website_name || brand.name}</h2>
            <p>
              {settings?.about_section?.text || "With over a decade of experience in elite physique conditioning and metabolic health, I have dedicated my career to simplifying the complex science of bodybuilding nutrition."}
            </p>
            <div className="coach-stats">
              {(settings?.about_section?.stats || coachStats).slice(0, 2).map((stat) => (
                <div key={stat.label}>
                  <strong>{stat.value}</strong>
                  <span>{stat.label}</span>
                </div>
              ))}
            </div>
            <div className="social-line">
              <span><a href="https://www.instagram.com/moatazhafez.mh/" target="_blank" rel="noreferrer">Instagram</a></span>
              <span> <a href="https://www.tiktok.com/@moatazhafez_mh" target="_blank" rel="noreferrer"> TikTok</a></span>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="home-section faq-section">
        <div className="container">
          <SectionHeading title="FAQ" />
          <motion.div className="faq-grid" initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.24 }} variants={stagger}>
            {faqItems.map((item) => (
              <motion.details className="faq-item" variants={fadeUp} key={item.question}>
                <summary>
                  <span>{item.question}</span>
                </summary>
                <p style={{paddingTop:"15px"}}>{item.answer}</p>
              </motion.details>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="home-cta" style={{ "--cta-image": `url(${images.detail})` }}>
        <motion.div className="container home-cta-inner" initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.35 }} transition={{ duration: 0.55 }}>
          <h2>Ready to stop guessing and start building?</h2>
          <p>{content.ctaSubtitle || "Secure your copy of the Ultimate Nutrition Blueprint and start your transformation today."}</p>
          {/* <div className="price-box">
            <span>{oldPrice}</span>
            <strong>{activePrice}</strong>
            <Button to="/shop">Buy Now</Button>
          </div> */}
          {/* <div className="cta-icons">
            <span>▣</span>
            <span>≡</span>
            <span>⌂</span>
          </div> */}
        </motion.div>
      </section>
    </div>
  );
}

function SectionHeading({ title }) {
  return (
    <div className="home-section-title">
      <h2>{title}</h2>
      <span />
    </div>
  );
}

function VideoCard({ video }) {
  const videoRef = useRef(null);

  const playVideo = () => {
    if (!videoRef.current || !video.src) return;
    videoRef.current.play().catch(() => {});
  };

  const pauseVideo = () => {
    if (!videoRef.current || !video.src) return;
    videoRef.current.pause();
    videoRef.current.currentTime = 0;
  };

  return (
    <motion.article
      className={`video-card${video.src ? " video-card-ready" : ""}`}
      variants={fadeUp}
      onMouseEnter={playVideo}
      onMouseLeave={pauseVideo}
      whileHover={{ y: -12 }}
      transition={{ duration: 0.28, ease: "easeOut" }}
    >
      {video.src ? (
        <video ref={videoRef} src={video.src} poster={video.poster} muted loop playsInline preload="metadata" />
      ) : (
        <img src={video.poster} alt={video.title} />
      )}
      <motion.span whileHover={{ scale: 1.15 }} aria-hidden="true">▶</motion.span>
      <strong>{video.title}</strong>
    </motion.article>
  );
}
